import { MarketingKids } from '../../../domain/marketing-kids';
import { CreateMarketingKidsPageItemApiModel, GenericLayoutApiModel, MarketingKidsPageItemApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  buttonMapperDomainToApi,
  createLocalizedText,
  descriptionLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
  titleLocalizedFromHeadlineMapper,
} from '../../mapping/create-tray-domain-to-api';
import { OrderedContentImage } from '../../../domain/content-image';
import {
  buttonMapperApiToDomain,
  commonMapper,
  descriptionLocalizedMapperApiToDomain,
  headlineTextMapper,
  mapApiModelImageToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { MarketingKidsTrayFormViewModel } from './marketing-kids-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { mapDomainButtonToButtonViewModel } from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  mapMarketingButtonToDomainButton,
  mapViewModelImageToDomainImageWithoutPlatforms,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';
import { ImageData } from '../../../domain/localized-domain-images';

// 'MarketingKids'

/**
 * DOMAIN -> API
 */

const mapDomainOrderedImageToApiId = (orderedContentImage: OrderedContentImage): string => {
  return orderedContentImage.image.id;
};

export const mapDomainKidsToApiModel = (
  kidsTray: MarketingKids,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingKidsPageItemApiModel => {
  const commonProperties = mapperPipe(
    kidsTray,
    scheduledDateMapperDomainToApi,
    titleLocalizedFromHeadlineMapper,
    descriptionLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    buttonMapperDomainToApi
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    layout: GenericLayoutApiModel.Centered,
    name: kidsTray.titleEnglish,
    icons: kidsTray.assetImages.map((it) => ({ imageId: it.id, iconText: createLocalizedText(it.en, it.ar) })),
    thumbnailsLeft: kidsTray.contentLeftImages.map(mapDomainOrderedImageToApiId),
    thumbnailsRight: kidsTray.contentRightImages.map(mapDomainOrderedImageToApiId),
    tenantId,
    platformIds: kidsTray.platformIds,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapApiKidsToDomain = (kids: MarketingKidsPageItemApiModel): MarketingKids => {
  const commonProperties = mapperPipe(
    kids,
    commonMapper,
    headlineTextMapper,
    buttonMapperApiToDomain,
    scheduledDateMapperApiToDomain,
    descriptionLocalizedMapperApiToDomain,
    targetingMapperApiToDomain
  );

  return remainderMapper(commonProperties, () => ({
    type: 'MarketingKids',
    titleEnglish: kids.name,
    assetImages: kids.icons.map((it) => ({
      ...mapApiModelImageToDomain(it.image),
      en: it.iconText.en,
      ar: it.iconText.ar,
    })),
    contentLeftImages: kids.thumbnailsLeft.map((it, idx) => ({
      order: idx,
      image: mapApiModelImageToDomain(it),
    })),
    // eslint-disable-next-line sonarjs/no-identical-functions
    contentRightImages: kids.thumbnailsRight.map((it, idx) => ({
      order: idx,
      image: mapApiModelImageToDomain(it),
    })),
  }));
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainMarketingKidsToViewModel = (kids: MarketingKids): MarketingKidsTrayFormViewModel => {
  return {
    id: kids.id,
    type: TrayFormViewModelType.KIDS,
    description: { en: kids.descriptionTextEnglish, ar: kids.descriptionTextArabic },
    headlineText: { en: kids.headlineTextEnglish, ar: kids.headlineTextArabic },
    countryIds: kids.countryIds,
    button: mapDomainButtonToButtonViewModel(kids.primaryButton),
    scheduling: {
      schedulingType: kids.scheduledFrom || kids.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: kids.scheduledFrom ? new Date(kids.scheduledFrom).toISOString() : undefined,
      scheduledTo: kids.scheduledTo ? new Date(kids.scheduledTo).toISOString() : undefined,
    },
    moduleName: kids.titleEnglish,
    userSegmentIds: kids.userSegmentIds,
    firstAssetImage: kids.assetImages[0],
    secondAssetImage: kids.assetImages[1],
    firstAssetText: { en: kids.assetImages[0].en, ar: kids.assetImages[0].ar },
    secondAssetText: { en: kids.assetImages[1].en, ar: kids.assetImages[1].ar },
    contentRightImages: kids.contentRightImages.map((it) => ({
      id: it.image.id,
      name: it.image.name,
      publicUrl: it.image.publicUrl,
    })),
    // eslint-disable-next-line sonarjs/no-identical-functions
    contentLeftImages: kids.contentLeftImages.map((it) => ({
      id: it.image.id,
      name: it.image.name,
      publicUrl: it.image.publicUrl,
    })),
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

const mapMarketingStudioTrayContentImages = (contentImages: ImageData[]): OrderedContentImage[] | OrderedContentImage[] => {
  return contentImages.map((image, order) => ({
    order,
    image: mapViewModelImageToDomainImageWithoutPlatforms(image),
  }));
};

export const mapViewModelKidsToDomain = (
  marketingKidsViewModel: MarketingKidsTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): MarketingKids => {
  return {
    id: marketingKidsViewModel.id,
    scheduledFrom: marketingKidsViewModel.scheduling?.scheduledFrom
      ? new Date(marketingKidsViewModel.scheduling.scheduledFrom).getTime()
      : undefined,
    scheduledTo: marketingKidsViewModel.scheduling?.scheduledTo
      ? new Date(marketingKidsViewModel.scheduling.scheduledTo).getTime()
      : undefined,
    titleEnglish: marketingKidsViewModel.moduleName,
    userSegmentIds: marketingKidsViewModel.userSegmentIds,
    countryIds: marketingKidsViewModel.countryIds,
    type: 'MarketingKids',
    order: undefined,
    visible: undefined,
    draggable: true,
    descriptionTextArabic: marketingKidsViewModel.description.ar,
    descriptionTextEnglish: marketingKidsViewModel.description.en,
    headlineTextArabic: marketingKidsViewModel.headlineText.ar,
    headlineTextEnglish: marketingKidsViewModel.headlineText.en,
    assetImages: [
      {
        id: marketingKidsViewModel.firstAssetImage.id,
        publicUrl: marketingKidsViewModel.firstAssetImage.publicUrl,
        name: marketingKidsViewModel.firstAssetImage.name,
        ar: marketingKidsViewModel.firstAssetText.ar,
        en: marketingKidsViewModel.firstAssetText.en,
      },
      {
        id: marketingKidsViewModel.secondAssetImage.id,
        publicUrl: marketingKidsViewModel.secondAssetImage.publicUrl,
        name: marketingKidsViewModel.secondAssetImage.name,
        ar: marketingKidsViewModel.secondAssetText.ar,
        en: marketingKidsViewModel.secondAssetText.en,
      },
    ],
    contentLeftImages: mapMarketingStudioTrayContentImages(marketingKidsViewModel.contentLeftImages) as OrderedContentImage[],
    contentRightImages: mapMarketingStudioTrayContentImages(marketingKidsViewModel.contentRightImages) as OrderedContentImage[],
    primaryButton: mapMarketingButtonToDomainButton(marketingKidsViewModel.button),
    platformIds: Object.values(platformIds),
  };
};
